import { Injectable, Injector, OnDestroy } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { BroadcastService } from '../broadcast/broadcast.service';
import { SwUpdate } from '@angular/service-worker';
import { WebPushService } from '../web-push/web-push.service';
import { CommonEvents } from '../../enums/common-events.enum';
import { AuthenticationService } from '../../../user/services/authentication.service';
import { LogService } from '../log/log.service';
import { NotificationService } from '@caslink/common-client/ng-app/notification/NotificationService';
import { environment } from '../../../../../environments/environment';
import { fromEvent, merge, Observable, Observer, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { EventTypes } from '@caslink/common-client';
import { DashboardService } from 'src/app/modules/devices/dashboard/services/dashboard.service';
import { IconService } from 'src/app/modules/user/services/icon.service';
import { BannerService } from '../banner/banner.service';
import { NoInternetMessageComponent } from '../../components/banner/messages/no-internet-message.component';
import { BannerMessage } from '../banner/banner-message';
import { NoServiceMessageComponent } from '../../components/banner/messages/no-service-message.component';
import { ThemeService } from '../../../common/services/theme/theme.service';
import { UserService } from 'src/app/modules/user/services/user.service';
import { defaultThemes } from '../../enums/themes.enum';
import { NewVersionAvailableMessageComponent } from '../../components/banner/messages/new-version-available-message.component';

@Injectable({
    providedIn: 'root'
})
export class AppStateService implements OnDestroy {

    public isReady: boolean;
    private connectionStatusSubscription: Subscription;
    private disconnectedFromServerSubscription: Subscription;
    isDesktop: boolean;

    constructor(
        private iconService: IconService,
        private deviceService: DeviceDetectorService,
        private bannerService: BannerService,
        private broadcastService: BroadcastService,
        private injector: Injector,
        private router: Router,
        private swUpdate: SwUpdate,
        private toastr: ToastrService,
        private webPushService: WebPushService,
        private authenticationService: AuthenticationService,
        private notificationService: NotificationService,
        private logger: LogService,
        private themeService: ThemeService,
        private userService: UserService
    ) {
        this.setIsDesktop();
        this.isUpdateAvailable();
        this.subscribeToInternetStatus();
        this.authenticate();
        this.subscribeToServiceConnectionEvents();
    }

    private _isSideNavOpened: boolean;
    private noInternetBanner = false;
    private noServiceConnectionBanner = false;

    public get isSideNavOpened() {
        return this._isSideNavOpened && this.router.url !== '';
    }

    public set isSideNavOpened(value) {
        this._isSideNavOpened = value;
    }

    public ngOnDestroy() {
        this.connectionStatusSubscription?.unsubscribe();
        this.disconnectedFromServerSubscription?.unsubscribe();
    }

    public isUpdateAvailable() {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.swUpdate.available.subscribe(() => {
            this.bannerService.openBanner(new BannerMessage(
                NewVersionAvailableMessageComponent,
                'DISMISS',
                'INSTALL UPDATE',
                () => window.location.reload(),
                'download',
                null,
                null,
                4,
                undefined,
                true,
                false,
                true
            ));
        });
    }

    private subscribeToServiceConnectionEvents() {
        this.disconnectedFromServerSubscription = this.broadcastService.on(EventTypes.DisconnectedFromServer).subscribe(() => {
            if (!this.noServiceConnectionBanner) {
                this.bannerService.openBanner(new BannerMessage(
                    NoServiceMessageComponent,
                    'DISMISS',
                    null,
                    null,
                    'signal_wifi_connected_no_internet_4',
                    null,
                    null,
                    2
                ));
                this.noServiceConnectionBanner = true;
            }
        });

        this.broadcastService.on(EventTypes.ReconnectedToServer).subscribe(() => {
            if (this.noServiceConnectionBanner) {
                this.bannerService.closeBanner(NoServiceMessageComponent);
                this.noServiceConnectionBanner = false;
            }
        });
    }

    private subscribeToInternetStatus() {
        this.hasInternetAccess.subscribe(hasAccess => {
            if (!hasAccess) {
                this.bannerService.openBanner(new BannerMessage(
                    NoInternetMessageComponent,
                    'DISMISS',
                    null,
                    null,
                    'wifi_off',
                    null,
                    null,
                    3,
                    undefined,
                    false,
                    false,
                    true
                ));
                this.noInternetBanner = true;
            } else if (this.noInternetBanner) {
                this.bannerService.closeBanner(NoInternetMessageComponent);
                this.noInternetBanner = false;
            }
        });
    }

    public get hasInternetAccess() {
        return merge<boolean>(
            fromEvent(window, 'offline').pipe(map(() => false)),
            fromEvent(window, 'online').pipe(map(() => true)),
            new Observable((sub: Observer<boolean>) => {
                sub.next(navigator.onLine);
                sub.complete();
            }));
    }

    private checkConnection() {
        this.connectionStatusSubscription = this.broadcastService.on(CommonEvents.ConnectionStatus)
            .subscribe((isConnectionAlive: boolean) => {
                if (isConnectionAlive) {
                    this.webPushService.subscribe();
                }
            });
        this.isReady = true;
    }

    private fetchLocations() {
        // This service is not injected in the constructor because it depends on NotificationService, which first must be initialized in authenticate().
        const dashboardService = this.injector.get<DashboardService>(DashboardService);
        if (dashboardService) {
            dashboardService.subscribeToDashboardUpdates();
        }
    }

    private async authenticate() {
        try {
            await this.authenticationService.authenticate();
            let isDarkThemeInApi: boolean;
            // just in case if local storage is out of sync with api we want to look for user preferences in api and reload  from there if diffferent with local storage
            this.userService.getUserpreferences().toPromise().then(result => {
                if (result != null && result.theme != null && result.theme !== '') {
                    isDarkThemeInApi = result.theme === defaultThemes.dark.name;
                    if (this.themeService.isDarkTheme !== isDarkThemeInApi) {
                        this.themeService.setDarkTheme(isDarkThemeInApi);
                    }
                }
            });


        } catch (e) {
            this.logger.log(e);
        } finally {
            this.checkConnection();
            this.iconService.tryRegisterSvgIcons();
            this.notificationService.initialize(environment.hub, /*options:*/ undefined, /*useImplicitAuth:*/ false, /*registerAlertHandler*/ false);
            this.fetchLocations();
            this.router.initialNavigation();
        }
    }

    private setIsDesktop() {
        const userAgent = window.navigator.userAgent;
        this.isDesktop = this.deviceService.isDesktop(userAgent);
    }
}
